/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable wc/guard-super-call */
import { createElement } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import CodeMirror, { EditorState, EditorView } from '@uiw/react-codemirror';
import * as allThemes from '@uiw/codemirror-themes-all';
import { StateEffect, StateField, type Extension } from '@codemirror/state';
import { Decoration, DecorationSet } from '@codemirror/view';
import { minimap } from '@replit/codemirror-minimap';
import { stateExtensions as jsonSchemaState } from 'codemirror-json-schema';
import type { JSONSchema7 } from 'json-schema';
import { json } from '@codemirror/lang-json';

import { BaseElementWithGlobalStyles, customElement } from '../../base-element';
import { watch } from '../../base-element/directives/watch.directive';

import styles from './code-editor.scss?inline';

export type { Extension };

// themes' list: https://uiwjs.github.io/react-codemirror/#/theme/home
export type ThemeNames =
  | 'abcdef'
  | 'abyss'
  | 'androidstudio'
  | 'andromeda'
  | 'atomone'
  | 'aura'
  | 'basicDark'
  | 'basicLight'
  | 'bbedit'
  | 'bespin'
  | 'consoleDark'
  | 'consoleLight'
  | 'copilot'
  | 'darcula'
  | 'dracula'
  | 'duotoneDark'
  | 'duotoneLight'
  | 'eclipse'
  | 'githubDark'
  | 'githubLight'
  | 'gruvboxDark'
  | 'gruvboxLight'
  | 'kimbie'
  | 'material'
  | 'materialDark'
  | 'materialLight'
  | 'monokai'
  | 'monokaiDimmed'
  | 'noctisLilac'
  | 'nord'
  | 'okaidia'
  | 'quietlight'
  | 'red'
  | 'solarizedDark'
  | 'solarizedLight'
  | 'sublime'
  | 'tokyoNight'
  | 'tokyoNightDay'
  | 'tokyoNightStorm'
  | 'tomorrowNightBlue'
  | 'vscodeDark'
  | 'vscodeLight'
  | 'whiteDark'
  | 'whiteLight'
  | 'xcodeDark'
  | 'xcodeLight';

export const codeEditorThemeList = Object.entries(allThemes)
  .filter(([_key, value]) => typeof value !== 'function')
  .map(([key]) => key)
  .filter((key) => !key.includes('Style'))
  .filter((key) => !key.includes('Init'))
  .filter((key) => !key.includes('defaultSettings')) as ThemeNames[];

const getThemeExtension = (theme?: ThemeNames): Extension | undefined => {
  if (!theme) return undefined;
  const themeKey = theme as keyof typeof allThemes;
  const extension = allThemes[themeKey];
  if (!extension || typeof extension === 'function') {
    return undefined;
  }
  return extension as Extension;
};

// Create error decoration effect
const addJsonErrors =
  StateEffect.define<{ from: number; to: number; message: string }[]>();

// Error decoration mark
const errorMark = Decoration.mark({
  class: 'cm-json-error',
  attributes: {
    style:
      'text-decoration: wavy underline red; text-decoration-thickness: 2px;',
  },
});

// State field to hold error decorations
const jsonErrorField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    let updatedDecorations = decorations.map(tr.changes);
    for (const effect of tr.effects) {
      if (effect.is(addJsonErrors)) {
        const marks = effect.value.map(({ from, to }) =>
          errorMark.range(from, to)
        );
        updatedDecorations = Decoration.set(marks);
      }
    }
    return updatedDecorations;
  },
  provide: (f) => EditorView.decorations.from(f),
});

// Custom JSON validator
const validateJSON = (
  text: string
): { from: number; to: number; message: string }[] => {
  if (!text.trim()) {
    return [];
  }

  try {
    JSON.parse(text);
    return [];
  } catch (error) {
    let from = 0;
    let to = text.length;
    let message = 'Invalid JSON';

    if (error instanceof SyntaxError) {
      const { message: errorMessage } = error;
      message = errorMessage;

      // Try to extract position from error message
      const posMatch = errorMessage.match(/position (\d+)/);
      if (posMatch) {
        from = parseInt(posMatch[1], 10);
        to = Math.min(from + 10, text.length);
      }
    }

    return [
      {
        from: Math.max(0, from),
        to: Math.max(from + 1, Math.min(to, text.length)),
        message,
      },
    ];
  }
};

@customElement('ps-code-editor')
export class CodeEditorWC extends BaseElementWithGlobalStyles {
  static styles = unsafeCSS(styles);

  root: Root;

  private editorTheme: Extension;

  private editorView?: EditorView;

  private detachEditorFocusHandlers?: () => void;

  @property({ type: String, reflect: true }) theme?: ThemeNames = 'basicLight';

  @watch('theme')
  handleThemeChange() {
    const extension = getThemeExtension(this.theme);
    if (extension) {
      this.editorTheme = extension;
      // Trigger a re-render when theme changes
      this.requestUpdate();
    }
  }

  @property() height?: string;

  @property({ type: Array }) extensions?: Extension[];

  @property() value?: string;

  @property({ type: Object }) onChange?: (value: string) => void;

  @property() name?: string;

  @property({ type: Boolean }) disabled?: boolean;

  @property({ type: Object }) jsonSchema?: JSONSchema7;

  @property({ type: Boolean }) enableJsonValidation?: boolean;

  private hiddenInput?: HTMLInputElement;

  private getDisplayValue(): string {
    if (this.value == null) {
      return '';
    }

    if (typeof this.value === 'string') {
      return this.value;
    }

    if (typeof this.value === 'object') {
      try {
        return JSON.stringify(this.value, null, 2);
      } catch (_error) {
        // Fallback to string coercion if object can't be stringified
        return String(this.value);
      }
    }

    return String(this.value);
  }

  // Helper to detect if content is JSON or if JSON extension is present
  private isJsonContent(): boolean {
    // First check if JSON validation is explicitly enabled
    if (this.enableJsonValidation === true) {
      return true;
    }

    if (this.enableJsonValidation === false) {
      return false;
    }

    // Check if JSON extension was explicitly provided
    if (this.extensions) {
      const hasJsonExt = this.extensions.some((ext) => {
        const extString = ext.toString();
        return extString.includes('json') || extString.includes('JSON');
      });
      if (hasJsonExt) {
        return true;
      }
    }

    // If no extension provided, detect based on content
    const editorValue = this.value;

    if (editorValue == null) {
      return false;
    }

    if (typeof editorValue === 'object') {
      return true;
    }

    const normalizedValue =
      typeof editorValue === 'string'
        ? editorValue
        : String(editorValue ?? '').toString();

    const trimmed = normalizedValue.trim();

    if (trimmed === '') {
      return false;
    }

    // Check if content looks like JSON (starts with { or [)
    return trimmed.startsWith('{') || trimmed.startsWith('[');
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.root) {
      this.root = createRoot(this.renderRoot);
    }
    // Initialize theme on connect - always set it
    const extension = getThemeExtension(this.theme);
    if (extension) {
      this.editorTheme = extension;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.detachEditorFocusHandlers) {
      this.detachEditorFocusHandlers();
      this.detachEditorFocusHandlers = undefined;
    }
    // Remove the hidden input
    if (this.hiddenInput) {
      this.hiddenInput.remove();
    }
  }

  private emitFocusEvent(type: 'focus' | 'blur') {
    this.dispatchEvent(
      new FocusEvent(type, {
        bubbles: true,
        composed: true,
      })
    );
  }

  private attachEditorFocusListeners(view: EditorView) {
    if (this.detachEditorFocusHandlers) {
      this.detachEditorFocusHandlers();
    }

    const target = view.contentDOM;

    const handleFocus = () => this.emitFocusEvent('focus');
    const handleBlur = () => this.emitFocusEvent('blur');

    target.addEventListener('focus', handleFocus, true);
    target.addEventListener('blur', handleBlur, true);

    this.detachEditorFocusHandlers = () => {
      target.removeEventListener('focus', handleFocus, true);
      target.removeEventListener('blur', handleBlur, true);
    };
  }

  private updateHiddenInput(value: string) {
    if (this.name && !this.hiddenInput) {
      this.hiddenInput = document.createElement('input');
      this.hiddenInput.type = 'hidden';
      this.hiddenInput.name = this.name;
      this.hiddenInput.value = this.value || '';
      this.appendChild(this.hiddenInput);
    }
    if (this.hiddenInput) {
      this.hiddenInput.value = value;
    }
  }

  public getValue() {
    return this.value;
  }

  render() {
    // Fallback: ensure theme is set if somehow not initialized
    if (!this.editorTheme) {
      const extension = getThemeExtension(this.theme);
      if (extension) {
        this.editorTheme = extension;
      }
    }

    const displayValue = this.getDisplayValue();

    // Build extensions array with JSON schema support if JSON is detected
    const hasJson = this.isJsonContent();

    // Filter out JSON lang extension if we're going to use jsonSchemaBundle
    // because jsonSchemaBundle includes JSON lang support
    const filteredExtensions = hasJson
      ? (this.extensions || []).filter((ext) => {
          const extString = ext.toString();
          return !(extString.includes('json') || extString.includes('JSON'));
        })
      : this.extensions || [];

    const allExtensions = [...filteredExtensions, minimap()];

    // Automatically add JSON schema validation when JSON is detected
    if (hasJson) {
      // Add JSON language support
      allExtensions.push(json());

      // Add our custom error decoration field
      allExtensions.push(jsonErrorField);

      // Add schema state if a schema is provided
      if (this.jsonSchema) {
        allExtensions.push(...jsonSchemaState(this.jsonSchema));
      }
    }

    this.root.render(
      createElement(CodeMirror, {
        height: this.height,
        extensions: allExtensions,
        value: displayValue,
        onCreateEditor: (view: EditorView, state: EditorState) => {
          this.editorView = view;
          this.attachEditorFocusListeners(view);

          if (displayValue) {
            this.updateHiddenInput(displayValue);
          }

          // Validate JSON and add error decorations
          if (hasJson && displayValue) {
            setTimeout(() => {
              const errors = validateJSON(displayValue || '');
              view.dispatch({
                effects: addJsonErrors.of(errors),
              });
            }, 100);
          }
        },
        onChange: (value: string) => {
          this.value = value;
          this.updateHiddenInput(value);

          // Re-validate JSON on change
          if (hasJson && this.editorView) {
            const errors = validateJSON(value);
            this.editorView.dispatch({
              effects: addJsonErrors.of(errors),
            });
          }

          if (this.onChange) {
            this.onChange(value);
          }
        },
        theme: this.editorTheme,
        editable: !this.disabled,
        basicSetup: {
          highlightActiveLine: !this.disabled,
          highlightActiveLineGutter: !this.disabled,
          lintKeymap: true,
        },
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ps-code-editor': CodeEditorWC;
  }
  enum PSElementTagNameMap {
    'ps-code-editor' = 'ps-code-editor',
  }
}
